// Filtering, counting and sorting for the roster chips. Kept separate from the
// chip components so both panels share one definition of what a filter means —
// the dock and the inbox previously drifted apart on exactly this.

import {
  FILTER_IDS,
  FILTER_DEFS,
  INSIGHT_FILTER_IDS,
  conflictsWith,
} from "./rosterTagDefs";

/**
 * Toggles one chip, or clears everything when `id` is null (the All chip).
 * Returns a new Set — the caller's is never mutated.
 *
 * @param {Set<string>} active
 * @param {string|null} id
 * @returns {Set<string>}
 */
export function toggleFilter(active, id) {
  if (id === null || id === undefined) return new Set();

  const next = new Set(active);
  if (next.has(id)) {
    next.delete(id);
    return next;
  }

  conflictsWith(id).forEach((other) => next.delete(other));
  next.add(id);
  return next;
}

/**
 * Segment ids for the roster's three-way control. These are the same filters as
 * everything else — the control is just a single-select view over two of them,
 * so one `matchesFilters` call still covers the whole state.
 */
export const SEGMENTS = [
  { id: "all", label: "All", filterId: null },
  { id: "unread", label: "Unread", filterId: FILTER_IDS.UNREAD },
  { id: "tagged", label: "Tagged", filterId: FILTER_IDS.TAGGED },
];

/**
 * Which segment the current filter set represents.
 * @param {Set<string>} active
 * @returns {string} One of SEGMENTS' ids.
 */
export function currentSegment(active) {
  if (active.has(FILTER_IDS.TAGGED)) return "tagged";
  if (active.has(FILTER_IDS.UNREAD)) return "unread";
  return "all";
}

/**
 * Switches segment without disturbing the coaching filters, so narrowing to
 * Unread keeps whatever signal filters were already on.
 *
 * @param {Set<string>} active
 * @param {string} segmentId
 * @returns {Set<string>} A new Set.
 */
export function setSegment(active, segmentId) {
  const next = new Set(active);
  next.delete(FILTER_IDS.UNREAD);
  next.delete(FILTER_IDS.TAGGED);
  const segment = SEGMENTS.find((s) => s.id === segmentId);
  if (segment?.filterId) next.add(segment.filterId);
  return next;
}

/**
 * Coaching filters currently on — what the Filters button counts. Excludes the
 * segment filters, which the segmented control already shows.
 *
 * @param {Set<string>} active
 * @returns {number}
 */
export function countActiveInsightFilters(active) {
  return [...active].filter((id) => FILTER_DEFS[id]?.needsInsights).length;
}

/**
 * @param {Set<string>} active
 * @returns {boolean} True when no chip is selected, i.e. "All" is showing.
 */
export function isAllActive(active) {
  return active.size === 0;
}

/**
 * @param {Set<string>} active
 * @returns {boolean} True when any coaching-signal chip is selected — the
 *   condition under which the panels swap to the staleness sort.
 */
export function hasInsightFilter(active) {
  return INSIGHT_FILTER_IDS.some((id) => active.has(id));
}

/**
 * AND across every active chip.
 *
 * @param {{clientId: string, unread: number, mentioned: boolean, flagged: boolean}} subject
 * @param {Object} tags - From `deriveTags`; EMPTY_TAGS when unresolved.
 * @param {Set<string>} active
 * @returns {boolean}
 */
export function matchesFilters(subject, tags, active) {
  for (const id of active) {
    const def = FILTER_DEFS[id];
    if (!def) continue;
    if (!def.matches(subject, tags)) return false;
  }
  return true;
}

/**
 * Per-chip match counts, each computed over the whole (search-narrowed) set and
 * independent of what is currently selected. Counting after the other active
 * filters would make every number collapse toward zero as chips are selected,
 * which reads as a bug rather than as information.
 *
 * @param {Array<Object>} subjects
 * @param {(clientId: string) => Object} getTags
 * @param {string[]} ids - Chips this panel renders.
 * @returns {Record<string, number>} Keyed by filter id, plus `all`.
 */
export function countMatches(subjects, getTags, ids) {
  const counts = { all: subjects.length };
  ids.forEach((id) => {
    counts[id] = 0;
  });

  subjects.forEach((subject) => {
    const tags = getTags(subject.clientId);
    ids.forEach((id) => {
      const def = FILTER_DEFS[id];
      if (def && def.matches(subject, tags)) counts[id] += 1;
    });
  });

  return counts;
}

/**
 * Most-neglected-first, falling through to the panel's existing comparator when
 * two rows are equally stale — so within a staleness band the familiar
 * mentions/unread/recency order is preserved.
 *
 * Ranking: never-assigned (Infinity) > oldest plan > … > freshest > no data.
 *
 * @param {(clientId: string) => Object} getTags
 * @param {(a: Object, b: Object) => number} tiebreak
 * @returns {(a: Object, b: Object) => number}
 */
export function compareByStalenessDesc(getTags, tiebreak) {
  return (a, b) => {
    const aStale = getTags(a.clientId).staleness;
    const bStale = getTags(b.clientId).staleness;
    if (aStale !== bStale) return bStale - aStale;
    return tiebreak(a, b);
  };
}

/**
 * Which derived flag says "we could not find out" for each coaching chip.
 * Chips whose section is unknown for every row are hidden rather than shown
 * with a count of zero — "Tracking (0)" reads as "nobody is tracking", which is
 * a very different statement from "we could not reach the tracking data".
 */
const UNKNOWN_FLAG_FOR = {
  [FILTER_IDS.DIET_STALE]: "dietUnknown",
  [FILTER_IDS.WORKOUT_STALE]: "workoutUnknown",
  [FILTER_IDS.NO_PLAN]: "planUnknown",
  [FILTER_IDS.TRACKING]: "trackingUnknown",
  [FILTER_IDS.NOT_TRACKING]: "trackingUnknown",
  [FILTER_IDS.NEW]: "attachmentUnknown",
};

/**
 * The chips worth rendering: every message-state chip, plus the coaching chips
 * whose section resolved for at least one row.
 *
 * @param {Array<Object>} subjects
 * @param {(clientId: string) => Object} getTags
 * @param {string[]} ids - Chips this panel could show, in order.
 * @returns {string[]} A subset of `ids`, order preserved.
 */
export function availableFilterIds(subjects, getTags, ids) {
  const undecided = new Set(
    ids.filter((id) => FILTER_DEFS[id]?.needsInsights)
  );
  if (undecided.size === 0) return ids;

  for (const subject of subjects) {
    if (undecided.size === 0) break;
    const tags = getTags(subject.clientId);
    for (const id of [...undecided]) {
      if (!tags[UNKNOWN_FLAG_FOR[id]]) undecided.delete(id);
    }
  }

  return ids.filter((id) => !undecided.has(id));
}

/** Copy preserved verbatim from the dock's existing empty states. */
const BUILT_IN_EMPTY = {
  [FILTER_IDS.TAGGED]: {
    title: "No unread tags",
    body: "Conversations with unread @-mentions of you appear here.",
  },
  [FILTER_IDS.UNREAD]: {
    title: "No unread conversations",
    body: "You're all caught up.",
  },
  [FILTER_IDS.FLAGGED]: {
    title: "No flagged conversations",
    body: "Conversations needing attention appear here.",
  },
  [FILTER_IDS.DIET_STALE]: {
    title: "No stale diet plans",
    body: "Everyone's diet plan is inside the window.",
  },
  [FILTER_IDS.WORKOUT_STALE]: {
    title: "No stale workout plans",
    body: "Everyone's workout plan is inside the window.",
  },
  [FILTER_IDS.NO_PLAN]: {
    title: "Everyone has a plan",
    body: "Clients with no diet or workout plan appear here.",
  },
  [FILTER_IDS.TRACKING]: {
    title: "Nobody is tracking yet",
    body: "Clients who logged food or a workout recently appear here.",
  },
  [FILTER_IDS.NOT_TRACKING]: {
    title: "Everyone is tracking",
    body: "Clients with no recent logs appear here.",
  },
  [FILTER_IDS.NEW]: {
    title: "No new clients",
    body: "Clients attached to you in the last week appear here.",
  },
};

/**
 * One empty-state ladder for both panels.
 *
 * @param {Set<string>} active
 * @param {string} query - Current search text.
 * @returns {{title: string, body: string}}
 */
export function describeEmptyState(active, query) {
  const needle = (query || "").trim();
  if (needle) {
    return { title: "No matches", body: `Nobody matches "${needle}".` };
  }

  if (active.size === 0) {
    return {
      title: "No conversations yet",
      body: "Clients appear here once they have a coaching room.",
    };
  }

  if (active.size === 1) {
    const [only] = active;
    if (BUILT_IN_EMPTY[only]) return BUILT_IN_EMPTY[only];
  }

  return {
    title: "No clients match these filters",
    body: "Try removing a filter.",
  };
}
